import { Pause, Play } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { Monitor } from '@/services/importacoes-service'
import { formatDateTime } from './run-labels'

interface MonitorsTableProps {
  monitors: Monitor[]
  canToggle: (monitor: Monitor) => boolean
  busyId?: string | null
  onToggle: (monitor: Monitor) => void
  emptyText: string
}

/** OABs ou processos monitorados, com pausar/retomar e a saúde da última consulta. */
export function MonitorsTable({ monitors, canToggle, busyId, onToggle, emptyText }: MonitorsTableProps) {
  if (monitors.length === 0) return <p className="text-sm text-muted-foreground">{emptyText}</p>
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Monitoramento</TableHead>
          <TableHead>Responsável</TableHead>
          <TableHead>Situação</TableHead>
          <TableHead>Última consulta</TableHead>
          <TableHead className="text-right">Ação</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {monitors.map((monitor) => (
          <TableRow key={monitor.id}>
            <TableCell className="font-medium tabular-nums">{monitor.label}</TableCell>
            <TableCell>{monitor.ownerName ?? '—'}</TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                <Badge variant={monitor.status === 'ATIVO' ? 'default' : 'outline'}>{monitor.status === 'ATIVO' ? 'Ativo' : 'Pausado'}</Badge>
                {monitor.falhasConsecutivas > 0 && (
                  <Badge variant="destructive" title={monitor.ultimoErro ?? undefined}>
                    {monitor.falhasConsecutivas} falha(s) seguida(s)
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell className="tabular-nums">{formatDateTime(monitor.ultimaExecucaoEm)}</TableCell>
            <TableCell className="text-right">
              {canToggle(monitor) && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === monitor.id}
                  onClick={() => onToggle(monitor)}
                  aria-label={`${monitor.status === 'ATIVO' ? 'Pausar' : 'Retomar'} ${monitor.label}`}
                >
                  {monitor.status === 'ATIVO' ? <><Pause /> Pausar</> : <><Play /> Retomar</>}
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
